import React, { createContext, useContext, useMemo, useState } from 'react';
import useAgentChat from '../../../hooks/useAgentChat';
import {
  AgentActivityItem,
  AgentConnectionStatus,
  AgentMessage,
  AgentRunState,
  AgentTimelineEvent,
  HitlState,
} from '../types';

interface HitlDecision {
  action: string;
  selected_option_key?: string;
  selected_ids?: string[];
  free_text?: string;
}

interface AgentRunContextValue {
  state: AgentRunState;
  sendMessage: (text: string) => void;
  resolveHitl: (decision: HitlDecision) => void;
  clearTimeline: () => void;
}

const AgentRunContext = createContext<AgentRunContextValue | null>(null);

function toTimelineEvent(input: {
  kind: AgentTimelineEvent['kind'];
  label: string;
}): AgentTimelineEvent {
  return {
    id: `evt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    kind: input.kind,
    label: input.label,
    timestamp: new Date().toISOString(),
  };
}

function normalizeMessages(messages: any[]): AgentMessage[] {
  return (messages || []).map((message) => ({
    id: message.id || `msg_${Math.random().toString(36).slice(2, 8)}`,
    role: message.role,
    createdAt: message.createdAt || new Date().toISOString(),
    text: message.text,
    answer: message.answer,
    request: message.request,
    code: message.code,
    turnId: message.turnId,
    checkpointId: message.checkpointId,
  }));
}

function normalizeActivityLog(activityLog: any[]): AgentActivityItem[] {
  return (activityLog || []).map((item) => ({
    id: item.id || `act_${Math.random().toString(36).slice(2, 8)}`,
    text: item.text || '',
    createdAt: item.createdAt || new Date().toISOString(),
  }));
}

function normalizeHitl(pendingHitl: any): HitlState | null {
  if (!pendingHitl) return null;
  return {
    checkpointId: pendingHitl.checkpointId || pendingHitl.checkpoint_id || '',
    turnId: pendingHitl.turnId || pendingHitl.turn_id,
    request: pendingHitl.request || {},
  };
}

function normalizeConnectionStatus(status: string): AgentConnectionStatus {
  if (status === 'open' || status === 'closed' || status === 'connecting') {
    return status;
  }
  return 'connecting';
}

export function AgentRunProvider({ children, initialThreadId = null }: { children: React.ReactNode; initialThreadId?: string | null }) {
  const { connectionStatus, threadId, messages, thinkingStep, activityLog, pendingHitl, isBusy, sendMessage, resolveHitl } = useAgentChat({
    threadId: initialThreadId,
  });
  const [timeline, setTimeline] = useState<AgentTimelineEvent[]>([]);

  React.useEffect(() => {
    if (!thinkingStep) return;
    setTimeline((prev) => [...prev, toTimelineEvent({ kind: 'thinking_step', label: thinkingStep })]);
  }, [thinkingStep]);

  React.useEffect(() => {
    const lastMessage = messages?.[messages.length - 1];
    if (!lastMessage) return;

    if (lastMessage.role === 'assistant') {
      setTimeline((prev) => [...prev, toTimelineEvent({ kind: 'final', label: 'Respuesta final emitida' })]);
    } else if (lastMessage.role === 'hitl') {
      setTimeline((prev) => [...prev, toTimelineEvent({ kind: 'hitl', label: 'Intervencion humana requerida' })]);
    } else if (lastMessage.role === 'error') {
      setTimeline((prev) => [...prev, toTimelineEvent({ kind: 'error', label: lastMessage.message || 'Error del agente' })]);
    }
  }, [messages]);

  const value = useMemo<AgentRunContextValue>(() => {
    const state: AgentRunState = {
      connectionStatus: normalizeConnectionStatus(connectionStatus),
      threadId,
      messages: normalizeMessages(messages),
      isBusy,
      thinkingStep,
      activityLog: normalizeActivityLog(activityLog),
      pendingHitl: normalizeHitl(pendingHitl),
      timeline,
    };

    return {
      state,
      sendMessage,
      resolveHitl,
      clearTimeline: () => setTimeline([]),
    };
  }, [connectionStatus, threadId, messages, isBusy, thinkingStep, activityLog, pendingHitl, timeline, sendMessage, resolveHitl]);

  return <AgentRunContext.Provider value={value}>{children}</AgentRunContext.Provider>;
}

export function useAgentRunContext() {
  const context = useContext(AgentRunContext);
  if (!context) {
    throw new Error('useAgentRunContext must be used within AgentRunProvider');
  }
  return context;
}
